<template>
    <BaseForm>
        <template #fields>
            <ComboboxField
                :value="selectedCategory"
                :label="$t('event-category')"
                :error-messages="errors.category"
                :items="CATEGORY_ITEMS"
                @change="handleConnectedCategoryCombobox($event)"
            />
            <ComboboxField
                :value="selectedType"
                :label="$t('event-type')"
                :error-messages="errors.type"
                :items="filteredTypeItems"
                @change="setFieldValue('type', $event?.id ?? '')"
                @blur="setFieldTouched('type', true)"
            />
            <CheckboxField
                :value="values.isPublic ?? undefined"
                :label="$t('is-public')"
                :error-messages="errors.isPublic"
                @change="setFieldValue('isPublic', ($event.target as HTMLInputElement).checked)"
                @blur="setFieldTouched('isPublic', true)"
            />
            <CheckboxField
                :value="values.isTechnical ?? undefined"
                :label="$t('is-technical')"
                :error-messages="errors.isTechnical"
                @change="setFieldValue('isTechnical', ($event.target as HTMLInputElement).checked)"
                @blur="setFieldTouched('isTechnical', true)"
            />
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import BaseForm from '@/components/Form/BaseForm.vue';
import CheckboxField from '@/components/Form/CheckboxField.vue';
import ComboboxField, { ComboboxItem } from '@/components/Form/ComboboxField.vue';
import { useEventSeriesInjection } from '@/event/composables/useEventSeriesInjection';
import { CATEGORY_ITEMS, CAT_TYPE_MAPPING, TYPE_ITEMS, getEventSeriesUpdateSchema } from '@/event/forms/schemas';
import { useForm } from '@/forms/composables/useForm';
import { computed } from 'vue';

const props = defineProps<{ seriesId: string }>();

const seriesId = computed(() => props.seriesId);
const { series, updateEventSeries } = useEventSeriesInjection();
const seriesUpdateSchema = computed(() => getEventSeriesUpdateSchema(seriesId.value));

const { errors, values, setFieldValue, setFieldTouched, validate, onDiscard, resetForm } = useForm(
    seriesUpdateSchema.value,
    computed(() => ({
        category: series.value?.category ?? '',
        type: series.value?.type ?? '',
        isPublic: series.value?.isPublic,
        isTechnical: series.value?.isTechnical,
    })),
);

const selectedCategory = computed(
    () => CATEGORY_ITEMS.find((i) => i.id === values.value.category) ?? { id: '', name: '' },
);
const filteredTypeItems = computed(() => {
    const catId = selectedCategory.value?.id ?? '';
    if (catId && catId !== '0') {
        const typeIds = CAT_TYPE_MAPPING[catId];
        return TYPE_ITEMS.filter((i) => typeIds.indexOf(i.id) >= 0);
    }
    return TYPE_ITEMS;
});
const selectedType = computed(
    () => filteredTypeItems.value.find((i) => i.id === values.value.type) ?? { id: '', name: '' },
);

function handleConnectedCategoryCombobox(item: ComboboxItem | null) {
    const categoryId = item?.id ?? '';
    setFieldValue('category', categoryId);
    // update type select because of different selectable Items based on category
    const typeId = selectedType.value?.id ?? '';
    setFieldValue('type', typeId);
}

async function save() {
    const valid = await validate();
    if (valid) {
        await updateEventSeries({ id: seriesId.value, ...values.value });
    }
}

defineExpose({ save, onDiscard, resetForm });
</script>
